import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../lib/supabase'

export default function AdminLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    checkUser()
  }, [])

  const isAdminRole = (role) => role === 'admin' || role === 'superadmin'

  const checkUser = async () => {
    try {
      const {
        data: { user }
      } = await supabase.auth.getUser()

      if (!user?.email) return

      const { data: profile, error: profileError } = await supabase
        .from('codevaro_users')
        .select('role, email, created_at')
        .eq('email', user.email)
        .maybeSingle()

      if (profileError) {
        console.error('Error checking user profile:', profileError)
        return
      }

      if (profile && isAdminRole(profile.role)) {
        navigate('/admin')
      }
    } catch (err) {
      console.error('Error checking user:', err)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (authError) throw authError

      if (!authData?.user?.email) {
        throw new Error('Login failed - no user data returned')
      }

      const { data: userProfile, error: profileError } = await supabase
        .from('codevaro_users')
        .select('role, email, created_at')
        .eq('email', authData.user.email)
        .maybeSingle()

      if (profileError) {
        console.error('Profile fetch error:', profileError)
        await supabase.auth.signOut()
        throw new Error('Σφάλμα κατά τον έλεγχο δικαιωμάτων χρήστη.')
      }

      if (!userProfile) {
        const { error: insertError } = await supabase.from('codevaro_users').insert([
          {
            email: authData.user.email,
            role: 'user',
            created_at: new Date().toISOString()
          }
        ])

        if (insertError) {
          console.error('Error creating user profile:', insertError)
          await supabase.auth.signOut()
          throw new Error('Failed to create user profile')
        }

        await supabase.auth.signOut()
        throw new Error('Access denied. Admin privileges required.')
      }

      if (!isAdminRole(userProfile.role)) {
        await supabase.auth.signOut()
        throw new Error('Access denied. Admin privileges required.')
      }

      navigate('/admin')
    } catch (err) {
      console.error('Login error:', err)
      setError(err.message || 'Login failed. Please try again.')

      try {
        await supabase.auth.signOut()
      } catch (signOutErr) {
        console.error('Error during sign out:', signOutErr)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access the admin dashboard
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}